import { useRef, useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartContainer from './ChartContainer';
import { useTheme } from '../../hooks/useTheme';
import { 
  createChartAnimations, 
  createHoverEffects, 
  createEnhancedTooltips,
  createMorphingAnimation,
  createInteractionEffects,
  ChartInteractionHandler
} from '../../utils/chartUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function DepartmentComparisonChart({ data, loading = false, comparisonType = 'utilization', onDepartmentClick }) {
  const chartRef = useRef();
  const [viewType, setViewType] = useState(comparisonType);
  const [animatingTransition, setAnimatingTransition] = useState(false);
  const [interactionHandler] = useState(() => new ChartInteractionHandler(chartRef, {
    enableDetailedViews: true,
    enableContextMenu: true
  }));
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [comparisonMode, setComparisonMode] = useState(false);
  const { theme } = useTheme();

  // Force chart update when theme changes
  useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current;
      // Update chart colors based on current theme
      chart.options.plugins.legend.labels.color = getComputedStyle(document.documentElement).getPropertyValue('--text').trim();
      chart.options.scales.x.ticks.color = getComputedStyle(document.documentElement).getPropertyValue('--muted').trim();
      chart.options.scales.y.ticks.color = getComputedStyle(document.documentElement).getPropertyValue('--muted').trim();
      chart.options.scales.x.grid.color = getComputedStyle(document.documentElement).getPropertyValue('--border').trim();
      chart.options.scales.y.grid.color = getComputedStyle(document.documentElement).getPropertyValue('--border').trim();
      chart.update('none');
    }
  }, [theme]);

  // Use real data or empty structure if no data provided
  const defaultData = {
    departments: [],
    metrics: {
      budget: [],
      spent: [],
      remaining: [],
      utilization: [],
      categories: {}
    }
  };

  const chartData = data || defaultData;

  const getChartConfig = () => {
    switch (viewType) {
      case 'budget_vs_spent':
        return {
          datasets: [
            {
              label: 'Budget',
              data: chartData.metrics.budget,
              backgroundColor: 'rgba(59, 130, 246, 0.6)',
              borderColor: 'rgba(59, 130, 246, 1)',
              borderWidth: 2,
              borderRadius: 4,
            },
            {
              label: 'Spent',
              data: chartData.metrics.spent,
              backgroundColor: 'rgba(34, 197, 94, 0.6)',
              borderColor: 'rgba(34, 197, 94, 1)',
              borderWidth: 2,
              borderRadius: 4,
            }
          ],
          yAxisCallback: (value) => '₹' + (value / 1000) + 'K'
        };
      
      case 'utilization':
        return {
          datasets: [
            {
              label: 'Budget Utilization %',
              data: chartData.metrics.utilization,
              backgroundColor: chartData.metrics.utilization.map(util => {
                if (util >= 90) return 'rgba(239, 68, 68, 0.7)'; // Red
                if (util >= 75) return 'rgba(245, 158, 11, 0.7)'; // Yellow
                return 'rgba(34, 197, 94, 0.7)'; // Green
              }),
              borderColor: chartData.metrics.utilization.map(util => {
                if (util >= 90) return 'rgba(239, 68, 68, 1)';
                if (util >= 75) return 'rgba(245, 158, 11, 1)';
                return 'rgba(34, 197, 94, 1)';
              }),
              borderWidth: 2,
              borderRadius: 4,
            }
          ],
          yAxisCallback: (value) => value + '%',
          yAxisMax: 100
        };
      
      case 'remaining':
        return {
          datasets: [
            {
              label: 'Remaining Budget',
              data: chartData.metrics.remaining,
              backgroundColor: 'rgba(168, 85, 247, 0.6)',
              borderColor: 'rgba(168, 85, 247, 1)',
              borderWidth: 2,
              borderRadius: 4,
            }
          ],
          yAxisCallback: (value) => '₹' + (value / 1000) + 'K'
        };
      
      default:
        return getChartConfig('budget_vs_spent');
    }
  };

  const config = getChartConfig();

  // Initialize interaction handlers
  useEffect(() => {
    if (interactionHandler) {
      // Register detailed view for departments
      interactionHandler.registerDetailView('default', ({ elementIndex, chartData }) => {
        const department = chartData.departments[elementIndex];
        return {
          type: 'department-detail',
          data: {
            name: department,
            budget: chartData.metrics.budget[elementIndex],
            spent: chartData.metrics.spent[elementIndex],
            utilization: chartData.metrics.utilization[elementIndex],
            categories: chartData.metrics.categories[department],
            trend: calculateDepartmentTrend(elementIndex)
          }
        };
      });

      // Register context menu for department comparison
      interactionHandler.registerContextMenu('default', ({ elementIndex, position }) => {
        console.log('Department context menu:', { elementIndex, position });
        // Could show options like:
        // - Compare with other departments
        // - View historical trends
        // - Set budget alerts
        // - Export department data
      });
    }
  }, [interactionHandler]);

  // Calculate department trend (mock implementation)
  const calculateDepartmentTrend = (elementIndex) => {
    // This would typically calculate based on historical data
    const utilization = chartData.metrics.utilization[elementIndex];
    return {
      direction: utilization > 70 ? 'increasing' : 'stable',
      rate: Math.random() * 10 // Mock trend rate
    };
  };

  // Enhanced department click handler
  const handleDepartmentClick = (event, elements) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const department = chartData.departments[elementIndex];
      
      // Handle multi-select for comparison mode
      if (event.ctrlKey || event.metaKey) {
        setSelectedDepartments(prev => 
          prev.includes(elementIndex)
            ? prev.filter(idx => idx !== elementIndex)
            : [...prev, elementIndex]
        );
        setComparisonMode(true);
        return;
      }

      // Use advanced click handler
      const result = interactionHandler.handleAdvancedClick(event, elements, chartData, {
        onDrillDown: () => {
          const departmentData = {
            name: department,
            budget: chartData.metrics.budget[elementIndex],
            spent: chartData.metrics.spent[elementIndex],
            utilization: chartData.metrics.utilization[elementIndex],
            categories: chartData.metrics.categories[department]
          };
          
          if (onDepartmentClick) {
            onDepartmentClick(departmentData);
          }
          
          return departmentData;
        }
      });
    }
  };

  const isDark = theme === 'dark';
  const textColor = isDark ? '#f8fafc' : '#1f2937';
  const mutedColor = isDark ? '#94a3b8' : '#6b7280';
  const borderColor = isDark ? '#334155' : '#e2e8f0';

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    ...createMorphingAnimation(animatingTransition ? 'elastic' : 'smooth'),
    ...createHoverEffects({
      enableGlow: true,
      enableScale: true,
      glowColor: isDark ? 'rgba(74, 222, 128, 0.3)' : 'rgba(34, 197, 94, 0.3)'
    }),
    ...createInteractionEffects({
      enableRipple: true
    }),
    ...createEnhancedTooltips({
      label: function(context) {
        const value = context.parsed.y;
        if (viewType === 'utilization') {
          return `${context.dataset.label}: ${value}%`;
        } else {
          return `${context.dataset.label}: ₹${value.toLocaleString('en-IN')}`;
        }
      },
      afterLabel: function(context) {
        const deptIndex = context.dataIndex;
        
        if (viewType === 'budget_vs_spent') {
          const utilization = chartData.metrics.utilization[deptIndex];
          return `Utilization: ${utilization}%`;
        } else if (viewType === 'utilization') {
          const budget = chartData.metrics.budget[deptIndex];
          const spent = chartData.metrics.spent[deptIndex];
          return [`Budget: ₹${budget.toLocaleString('en-IN')}`, `Spent: ₹${spent.toLocaleString('en-IN')}`];
        }
        
        return '';
      },
      footer: function(tooltipItems) {
        return 'Click to view department details';
      }
    }),
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: textColor,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          color: mutedColor
        },
        grid: {
          color: borderColor,
          display: false
        }
      },
      y: {
        beginAtZero: true,
        max: config.yAxisMax,
        ticks: {
          color: mutedColor,
          callback: config.yAxisCallback
        },
        grid: {
          color: borderColor
        }
      }
    },
    onClick: handleDepartmentClick
  };

  const chartDataConfig = {
    labels: chartData.departments,
    datasets: config.datasets.map(dataset => ({
      ...dataset,
      // Enhance styling for selected departments
      backgroundColor: Array.isArray(dataset.backgroundColor) 
        ? dataset.backgroundColor.map((color, index) => {
            const isSelected = selectedDepartments.includes(index);
            const alpha = isSelected ? 0.9 : 0.6;
            return color.replace(/[\d.]+\)$/g, `${alpha})`);
          })
        : dataset.backgroundColor,
      borderWidth: chartData.departments.map((_, index) => 
        selectedDepartments.includes(index) ? 4 : 2
      ),
      borderColor: Array.isArray(dataset.borderColor)
        ? dataset.borderColor.map((color, index) => {
            return selectedDepartments.includes(index) ? '#ffffff' : color;
          })
        : dataset.borderColor
    }))
  };

  const handleViewTypeChange = (newViewType) => {
    if (newViewType !== viewType) {
      setAnimatingTransition(true);
      setViewType(newViewType);
      
      // Reset animation state after transition
      setTimeout(() => {
        setAnimatingTransition(false);
      }, 600);
    }
  };

  const getViewTypeTitle = () => {
    switch (viewType) {
      case 'budget_vs_spent':
        return 'Budget vs Spending Comparison';
      case 'utilization':
        return 'Budget Utilization Rates';
      case 'remaining':
        return 'Remaining Budget Analysis';
      default:
        return 'Department Comparison';
    }
  };

  const getViewTypeSubtitle = () => {
    switch (viewType) {
      case 'budget_vs_spent':
        return 'Compare allocated budget against actual spending';
      case 'utilization':
        return 'Budget utilization percentage by department';
      case 'remaining':
        return 'Available budget remaining for each department';
      default:
        return 'Interactive department analysis';
    }
  };

  if (loading) {
    return (
      <ChartContainer
        title={getViewTypeTitle()}
        subtitle={getViewTypeSubtitle()}
        loading={true}
        chartRef={chartRef}
        chartData={chartDataConfig}
      >
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--primary)' }}></div>
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer
      title={getViewTypeTitle()}
      subtitle={getViewTypeSubtitle()}
      chartRef={chartRef}
      chartData={chartDataConfig}
      enableAnimations={true}
    >
      <div className="space-y-4">
        {/* Comparison Mode Controls */}
        {comparisonMode && selectedDepartments.length > 0 && (
          <div className="mb-4 p-3 rounded-lg border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                Comparing {selectedDepartments.length} departments
              </span>
              <button
                onClick={() => {
                  setSelectedDepartments([]);
                  setComparisonMode(false);
                }}
                className="px-3 py-1 rounded text-sm hover:opacity-80"
                style={{ background: 'var(--border)', color: 'var(--text)' }}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* View Type Selector */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => handleViewTypeChange('budget_vs_spent')}
            className={`px-3 py-1 rounded text-sm font-medium transition-all duration-300 ${
              viewType === 'budget_vs_spent' 
                ? 'text-white transform scale-105' 
                : 'hover:opacity-80'
            }`}
            style={{ 
              background: viewType === 'budget_vs_spent' ? 'var(--primary)' : 'var(--border)',
              color: viewType === 'budget_vs_spent' ? 'white' : 'var(--text)'
            }}
          >
            Budget vs Spent
          </button>
          <button
            onClick={() => handleViewTypeChange('utilization')}
            className={`px-3 py-1 rounded text-sm font-medium transition-all duration-300 ${
              viewType === 'utilization' 
                ? 'text-white transform scale-105' 
                : 'hover:opacity-80'
            }`}
            style={{ 
              background: viewType === 'utilization' ? 'var(--primary)' : 'var(--border)',
              color: viewType === 'utilization' ? 'white' : 'var(--text)'
            }}
          >
            Utilization %
          </button>
          <button
            onClick={() => handleViewTypeChange('remaining')}
            className={`px-3 py-1 rounded text-sm font-medium transition-all duration-300 ${
              viewType === 'remaining' 
                ? 'text-white transform scale-105' 
                : 'hover:opacity-80'
            }`}
            style={{ 
              background: viewType === 'remaining' ? 'var(--primary)' : 'var(--border)',
              color: viewType === 'remaining' ? 'white' : 'var(--text)'
            }}
          >
            Remaining Budget
          </button>
        </div>

        {/* Chart */}
        <div className="h-64">
          <Bar ref={chartRef} data={chartDataConfig} options={options} />
        </div>

        {/* Department Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {chartData.departments.map((dept, index) => {
            const budget = chartData.metrics.budget[index];
            const spent = chartData.metrics.spent[index];
            const utilization = chartData.metrics.utilization[index];
            const remaining = chartData.metrics.remaining[index];
            
            const getStatusColor = () => {
              if (utilization >= 90) return 'text-red-600';
              if (utilization >= 75) return 'text-yellow-600';
              return 'text-green-600';
            };
            
            return (
              <div 
                key={dept}
                className="p-4 rounded-lg border cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                style={{ 
                  background: 'var(--panel)', 
                  borderColor: 'var(--border)' 
                }}
                onClick={() => handleDepartmentClick(null, [{ index }])}
              >
                <div className="text-center">
                  <h4 className="font-semibold mb-2" style={{ color: 'var(--text)' }}>
                    {dept}
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--muted)' }}>Budget:</span>
                      <span style={{ color: 'var(--text)' }}>₹{(budget / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--muted)' }}>Spent:</span>
                      <span style={{ color: 'var(--text)' }}>₹{(spent / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--muted)' }}>Remaining:</span>
                      <span style={{ color: 'var(--text)' }}>₹{(remaining / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span style={{ color: 'var(--muted)' }}>Utilization:</span>
                      <span className={getStatusColor()}>{utilization}%</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ChartContainer>
  );
}

export default DepartmentComparisonChart;